import { sha256 } from "js-sha256";
import { BEHAVIOR, BIOLUMINESCENT, CONSERVATION_STATUS, DEFAULT_TAGS, DORSAL_FIN_SPINES, FLATTENED_BODY, getEnumCategory, NUM_GILLS, REPRODUCTIVE_STRATEGY, SPECIES_TYPE, TEMPERATURE_REGULATION, FEEDING_BEHAVIOR, GROUP_BEHAVIOR, NUM_DORSAL_FINS, ANAL_FIN, SPIRACLES, NICTITATING_MEMBRANE, CAUDAL_FIN_SHAPE, MOUTH_IN_FRONT_OF_EYES, PROXIMAL_DORSAL_FINS, HABITAT, WATER_COLUMN, OCEAN_ZONE, DEPTH_ZONE } from "../constants/enums";
import { getIUCNGraphic, getIUCNDescription } from "../utils/iucn-graphics";
import { DEFAULT_SOLID_PATTERN, DEFAULT_STROKE_OPACITY, DEFAULT_STROKE_WIDTH, HIGHLIGHTED_STROKE_WIDTH } from "../constants/style";
import { DEFAULT_NODE_COLOR, DEFAULT_PATH_COLOR } from "../constants/colors";
// SharkTreeLeafNode
export class SharkSpecies {
    constructor(config, parent = null, speciesType = SPECIES_TYPE.SHARKS) {
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "parent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "speciesType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commonName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "alternativeNames", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "binomialName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "imageUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "imageUrls", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "domain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "kingdom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "phylum", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "class", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "subclass", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "subdivision", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "superorder", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "order", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "family", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "genus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "species", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "tags", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "depthRange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "x", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "y", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "parentPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "node", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "hash", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "index", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.config = config;
        this.parent = parent;
        this.alternativeNames = [];
        this.speciesType = speciesType;
        this.parentPath = [];
        this.tags = config.tags || [];
        this.buildShark();
        this.hash = this.getHash();
    }
    /*----------------------------------------|
    |                GETTERS                  |
    |----------------------------------------*/
    getX() {
        return this.x;
    }
    getY() {
        return this.y;
    }
    getParentPath() {
        return this.parentPath;
    }
    getLastParentPathSegment() {
        return this.parentPath[this.parentPath.length - 1];
    }
    getParent() {
        return this.parent;
    }
    getNode() {
        return this.node;
    }
    getHash() {
        if (this.hash)
            return this.hash;
        const jsonString = JSON.stringify(this.config);
        return sha256(jsonString).toString();
    }
    getHashChain() {
        let hashChain = [this.getHash()];
        let parent = this.getParent();
        while (parent !== null) {
            hashChain.push(parent.getHash());
            parent = parent.getParent();
        }
        return hashChain;
    }
    getParentHash() {
        var _a;
        return (_a = this.parent) === null || _a === void 0 ? void 0 : _a.getHash();
    }
    getRelatedSpecies(allSpecies) {
        return allSpecies.filter(s => s !== this &&
            (s.genus === this.genus || s.family === this.family) &&
            s.binomialName !== this.binomialName).slice(0, 3); // Limit to 3 for brevity
    }
    hasUnusualTemperatureRegulation() {
        return this.tags.some(tag => [TEMPERATURE_REGULATION.ENDOTHERMIC, TEMPERATURE_REGULATION.REGIONALLY_ENDOTHERMIC].includes(tag));
    }
    hasCriticalConservationStatus() {
        return this.tags.some(tag => [
            CONSERVATION_STATUS.CR,
            CONSERVATION_STATUS.EN,
            CONSERVATION_STATUS.VU
        ].includes(tag));
    }
    hasUniquePhysicalTrait() {
        return this.tags.some(tag => [
            BIOLUMINESCENT.YES,
            DORSAL_FIN_SPINES.YES,
            DORSAL_FIN_SPINES.ONLY_ON_FIRST,
            FLATTENED_BODY.YES,
            NUM_GILLS.SIX,
            NUM_GILLS.SEVEN
        ].includes(tag));
    }
    hasNotableReproduction() {
        return this.tags.some(tag => [
            REPRODUCTIVE_STRATEGY.OVIPAROUS,
            REPRODUCTIVE_STRATEGY.VIVIPAROUS,
            REPRODUCTIVE_STRATEGY.OVOVIVIPAROUS,
            REPRODUCTIVE_STRATEGY.OOPHAGY,
            REPRODUCTIVE_STRATEGY.EMBRYOPHAGY
        ].includes(tag));
    }
    /*----------------------------------------|
    |                SETTERS                  |
    |----------------------------------------*/
    setX(x) {
        this.x = x;
    }
    setY(y) {
        this.y = y;
    }
    setNode(node) {
        this.node = node;
    }
    setIndex(index) {
        this.index = index;
    }
    setNodeOpacity(opacity) {
        var _a;
        (_a = this.node) === null || _a === void 0 ? void 0 : _a.setAttribute("fill-opacity", `${opacity}`);
    }
    /*----------------------------------------|
    |                ADDERS                   |
    |----------------------------------------*/
    addParentPathSegment(parentPathSegment) {
        this.parentPath.push(parentPathSegment);
    }
    addParentPathToParentChildPath() {
        var _a;
        const parentPath = this.getParentPath();
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.addChildPath(parentPath);
    }
    /*----------------------------------------|
    |              HIGHLIGHTING               |
    |----------------------------------------*/
    highlightNode(color = DEFAULT_NODE_COLOR) {
        var _a;
        (_a = this.node) === null || _a === void 0 ? void 0 : _a.setAttribute("fill", color);
    }
    highlightParentPath(strokeWidth = HIGHLIGHTED_STROKE_WIDTH, strokeColor = DEFAULT_PATH_COLOR, strokeOpacity = DEFAULT_STROKE_OPACITY, dashPattern = DEFAULT_SOLID_PATTERN) {
        this.parentPath.forEach(segment => {
            if (!strokeColor) {
                const previousStrokeColor = segment.getAttribute("stroke");
                if (!previousStrokeColor)
                    segment.setAttribute("stroke", DEFAULT_PATH_COLOR);
            }
            else
                segment.setAttribute("stroke", strokeColor);
        });
        this.parentPath.forEach(segment => {
            if (!strokeWidth) {
                const previousStrokeWidth = segment.getAttribute("stroke-width");
                if (!previousStrokeWidth)
                    segment.setAttribute("stroke-width", `${DEFAULT_STROKE_WIDTH}`);
            }
            else
                segment.setAttribute("stroke-width", `${strokeWidth}`);
        });
        this.parentPath.forEach(segment => {
            if (!strokeOpacity) {
                const previousStrokeOpacity = segment.getAttribute("stroke-opacity");
                if (!previousStrokeOpacity)
                    segment.setAttribute("stroke-opacity", `${DEFAULT_STROKE_OPACITY}`);
            }
            else
                segment.setAttribute("stroke-opacity", `${strokeOpacity}`);
        });
        this.parentPath.forEach(segment => dashPattern === "solid" ? segment.removeAttribute("stroke-dasharray") : segment.setAttribute("stroke-dasharray", dashPattern));
    }
    buildShark() {
        this.commonName = this.config.commonName;
        this.alternativeNames = this.config.alternativeNames || [];
        this.binomialName = this.config.binomialName;
        if (this.config.imageUrl)
            this.imageUrl = this.config.imageUrl;
        if (this.config.imageUrls)
            this.imageUrls = this.config.imageUrls;
        if (this.config.depthRange)
            this.depthRange = this.config.depthRange;
        this.domain = this.config.domain;
        this.kingdom = this.config.kingdom;
        this.phylum = this.config.phylum;
        this.class = this.config.class;
        if (this.config.subclass)
            this.subclass = this.config.subclass;
        if (this.config.subdivision)
            this.subdivision = this.config.subdivision;
        if (this.config.superorder)
            this.superorder = this.config.superorder;
        this.order = this.config.order;
        this.family = this.config.family;
        this.genus = this.config.genus;
        this.species = this.config.species;
        this.buildTags();
    }
    buildTags() {
        this.tags = this.config.tags || [];
        const existingCategories = new Set();
        this.tags.forEach(tag => {
            const category = getEnumCategory(tag);
            if (category)
                existingCategories.add(category);
        });
        this.addDepthZoneTag();
        const defaultTags = this.speciesType === SPECIES_TYPE.RAYS ? DEFAULT_TAGS.rays : (this.speciesType === SPECIES_TYPE.SHARKS ? DEFAULT_TAGS.sharks : DEFAULT_TAGS.chimaeras);
        const tagsToAdd = defaultTags.filter(defaultTag => {
            const category = getEnumCategory(defaultTag);
            return category && !existingCategories.has(category);
        });
        this.tags = [...this.tags, ...tagsToAdd];
    }
    addDepthZoneTag() {
        var _a;
        const [minDepth, maxDepth] = ((_a = this.depthRange) === null || _a === void 0 ? void 0 : _a.split('-').map(d => parseInt(d.trim()))) || [0, 0];
        // Add tags for all depth zones that the species overlaps with
        if (minDepth <= 200) {
            this.tags.push(DEPTH_ZONE.SHALLOW_WATER);
        }
        if (minDepth <= 1000 && maxDepth >= 200) {
            this.tags.push(DEPTH_ZONE.MID_WATER);
        }
        if (minDepth <= 4000 && maxDepth >= 1000) {
            this.tags.push(DEPTH_ZONE.DEEP_WATER);
        }
        if (minDepth <= 6000 && maxDepth >= 4000) {
            this.tags.push(DEPTH_ZONE.ABYSSAL);
        }
        if (maxDepth >= 6000) {
            this.tags.push(DEPTH_ZONE.HADAL);
        }
    }
    /*----------------------------------------|
    |              PRINTING                   |
    |----------------------------------------*/
    getFormattedString(allSpecies) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const relatedSpecies = this.getRelatedSpecies(allSpecies);
        const alternativeNamesSentence = this.getAlternativeNamesSentence();
        const alternativeNamesString = alternativeNamesSentence.length > 0
            ? `<strong>Alternative Names:</strong> ${alternativeNamesSentence}`
            : "";
        const conservationStatusTags = this.tags.filter(tag => [
            CONSERVATION_STATUS.CR, CONSERVATION_STATUS.EN, CONSERVATION_STATUS.VU, CONSERVATION_STATUS.DD, CONSERVATION_STATUS.LC, CONSERVATION_STATUS.EW, CONSERVATION_STATUS.NT
        ].includes(tag)).slice(0, 1);
        const conservationStatusString = conservationStatusTags.length > 0
            ? `<div class="section conservation-status">
                <strong>Conservation Status:</strong> 
                <span class="iucn-status">
                    <span class="iucn-text">${getIUCNDescription(conservationStatusTags[0])}</span>
                    ${getIUCNGraphic(conservationStatusTags[0])}
                </span>
            </div>`
            : "";
        const depthRangeString = this.depthRange
            ? `<div class="section"><strong>Depth Range:</strong> ${this.depthRange}</div>`
            : `<div class="section"><strong>Depth Range:</strong> Unknown</div>`;
        const descriptionSentence = this.getDescription();
        const descriptionString = descriptionSentence.length > 0
            ? `<div class="section"><strong>Description:</strong> ${descriptionSentence}</div>`
            : "";
        return `
            <h2>${this.commonName}</h2>
            <div class="section">
                <strong>Binomial Name:</strong> ${this.binomialName}
                <br>${alternativeNamesString}
            </div>
            ${conservationStatusString}
            ${depthRangeString}
            ${descriptionString}
            <div class="section">
                <strong>Taxonomy:</strong><br>
                Domain: ${this.domain}${(_a = this.getCommonNameFromTaxonomicGroup(this.domain)) !== null && _a !== void 0 ? _a : ''}<br>
                Kingdom: ${this.kingdom}${(_b = this.getCommonNameFromTaxonomicGroup(this.kingdom)) !== null && _b !== void 0 ? _b : ''}<br>
                Phylum: ${this.phylum}${(_c = this.getCommonNameFromTaxonomicGroup(this.phylum)) !== null && _c !== void 0 ? _c : ''}<br>
                Class: ${this.class}${(_d = this.getCommonNameFromTaxonomicGroup(this.class)) !== null && _d !== void 0 ? _d : ''}<br>
            `
            + (this.superorder ? `Superorder: ${this.superorder}${(_e = this.getCommonNameFromTaxonomicGroup(this.superorder)) !== null && _e !== void 0 ? _e : ''}<br>` : "")
            + `
                Order: ${this.order}${(_f = this.getCommonNameFromTaxonomicGroup(this.order)) !== null && _f !== void 0 ? _f : ''}<br>
                Family: ${this.family}${(_g = this.getCommonNameFromTaxonomicGroup(this.family)) !== null && _g !== void 0 ? _g : ''}<br>
                Genus: ${this.genus}${(_h = this.getCommonNameFromTaxonomicGroup(this.genus)) !== null && _h !== void 0 ? _h : ''}<br>
                Species: ${this.species}
            </div>
            <div class="section">
                <strong>Related Species:</strong>
                ${relatedSpecies.length > 0 ? `<ul>${relatedSpecies.map(s => `<li>${s.commonName} (${s.binomialName})</li>`).join("")}</ul>` : "None in this dataset"}
            </div>
        `;
    }
    getCommonNameFromTaxonomicGroup(taxonomicGroup) {
        const commonNames = {
            "Eukaryota": "Eukaryotes",
            "Animalia": "Animals",
            "Chordata": "Vertebrates",
            "Chondrichthyes": "Cartilagenous Fishes",
            // ----------------------------------
            "Squalomorphii": "Squalean Sharks",
            "Galeomorphii": "Galean Sharks",
            // ----------------------------------//
            "Carcharhiniformes": "Ground Sharks",
            "Lamniformes": "Mackerel Sharks",
            "Squatiniformes": "Angelsharks",
            "Heterodontiformes": "Bullhead Sharks",
            "Orectolobiformes": "Carpet Sharks",
            "Pristiophoriformes": "Sawsharks",
            "Squaliformes": "Dogfish Sharks",
            "Hexanchiformes": "Cow Sharks and Frilled Sharks",
            "Rajiformes": "Skates",
            "Rhinopristiformes": "Rhino Rays",
            "Torpediniformes": "Electric Rays",
            "Myliobatiformes": "Stingrays",
            "Chimaeriformes": "Chimaeras",
            // ----------------------------------//
            "Alopiidae": "Thresher Sharks",
            "Lamnidae": "Mackerel Sharks",
            "Carchariidae": "Sand Tiger Sharks",
            "Cetorhinidae": "Basking Sharks",
            "Megachasmidae": "Megamouth Sharks",
            "Mitsukurinidae": "Goblin Sharks",
            "Odontaspididae": "Sand Sharks",
            "Pseudocarchariidae": "Crocodile Sharks",
            "Stegostomatidae": "Zebra Sharks",
            "Ginglymostomatidae": "Nurse Sharks",
            "Rhincodontidae": "Whale Sharks",
            "Parascylliidae": "Collared Carpet Sharks",
            "Orectolobidae": "Wobbegong Sharks",
            "Hemiscylliidae": "Bamboo Sharks",
            "Brachaeluridae": "Blind Sharks",
            "Hemigaleidae": "Weasel Sharks",
            "Atelomycteridae": "Colored Catsharks",
            "Pentanchidae": "Deepwater Catsharks",
            "Dichichthyidae": "Bristle Sharks",
            "Scyliorhinidae": "Catsharks",
            "Pseudotriakidae": "False Catsharks",
            "Proscylliidae": "Finback Catsharks",
            "Leptochariidae": "Barbeled Houndsharks",
            "Triakidae": "Houndsharks",
            "Carcharhinidae": "Requiem Sharks",
            "Sphyrnidae": "Hammerhead Sharks",
            "Galeocerdonidae": "Tiger Sharks",
            "Hexanchidae": "Cow Sharks",
            "Chlamydoselachidae": "Frilled Sharks",
            "Etmopteridae": "Lantern Sharks",
            "Somniosidae": "Sleeper Sharks",
            "Oxynotidae": "Roughsharks",
            "Centrophoridae": "Gulper Sharks",
            "Dalatiidae": "Kitefin Sharks",
            "Squalidae": "Dogfish Sharks",
            "Echinorhinidae": "Bramble Sharks",
            "Anacanthobatidae": "Smooth Skates",
            "Arhynchobatidae": "Softnose Skates",
            "Gurgesiellidae": "Pygmy Skates",
            "Rajidae": "Skates",
            "Trygonorrhinidae": "Fiddler Rays or Banjo Rays",
            "Rhinobatidae": "Guitarfishes",
            "Rhinidae": "Bowmouth Guitarfishes or Wedgefishes",
            "Glaucostegidae": "Giant Guitarfishes",
            "Pristidae": "Sawfishes",
            "Zanobatidae": "Pan Rays",
            "Platyrhinidae": "Thornback Rays",
            "Narkidae": "Sleeper Rays",
            "Narcinidae": "Electric Rays",
            "Hypnidae": "Coffin Rays",
            "Torpedinidae": "Torpedo Rays",
            "Hexatrygonidae": "Sixgill Stingrays",
            "Dasyatidae": "Whiptail Stingrays",
            "Potamotrygonidae": "Neotropical Stingrays",
            "Urotrygonidae": "American Round Stingrays",
            "Gymnuridae": "Butterfly Rays",
            "Plesiobatidae": "Deepwater Stingrays or Giant Stingarees",
            "Urolophidae": "Round Stingrays or Stingarees",
            "Aetobatidae": "Pelagic Eagle Rays",
            "Myliobatidae": "Eagle Rays",
            "Mobulidae": "Mantas or Devil Rays",
            "Rhinopteridae": "Cownose Rays",
            "Chimaeridae": "Short-Nosed Chimaeras",
            "Callorhinchidae": "Plough-Nosed Chimaeras or Elephantfish",
            "Rhinochimaeridae": "Long-Nosed Chimaeras",
            // ----------------------------------//
            "Chiloscyllium": "Bamboo Sharks",
            "Cephaloscyllium": "Swellsharks",
            "Rhizoprionodon": "Sharpnose Sharks",
            "Mustelus": "Smooth-Hounds",
            "Scoliodon": "Spadenose Sharks",
            "Negaprion": "Lemon Sharks",
            "Squalus": "Spurdogs",
            "Neotrygon": "Maskrays",
            "Himantura": "Whiprays",
            "Dasyatis": "Stingrays"
            // ----------------------------------//
        };
        if (taxonomicGroup in commonNames)
            return ` (${commonNames[taxonomicGroup]})`;
        else
            return '';
    }
    getAlternativeNamesSentence() {
        let alternativeNamesString = "";
        const numAlternativeNames = this.alternativeNames.length;
        if (numAlternativeNames) {
            this.alternativeNames.forEach((alternativeName, i) => {
                alternativeNamesString += `${alternativeName}`;
                if (i !== this.alternativeNames.length - 1) {
                    const includeComma = numAlternativeNames !== 2;
                    const includeOr = i === numAlternativeNames - 2;
                    alternativeNamesString += `${includeComma ? "," : ""} ${includeOr ? "or " : ""}`;
                }
            });
            return `The ${this.commonName} is also known as the ${alternativeNamesString}.`;
        }
        return alternativeNamesString;
    }
    getDescription() {
        const descriptionParts = [];
        const addPart = (part) => {
            if (part.trim())
                descriptionParts.push(part.trim());
        };
        // Start with taxonomic context and physical characteristics
        const taxonomicIntro = this.getTaxonomicIntroduction();
        if (taxonomicIntro)
            addPart(taxonomicIntro);
        // Physical characteristics and morphology
        const physicalTraits = this.getPhysicalCharacteristics();
        if (physicalTraits)
            addPart(physicalTraits);
        // Habitat and distribution
        const habitatInfo = this.getHabitatInformation();
        if (habitatInfo)
            addPart(habitatInfo);
        // Feeding behavior and ecology
        const feedingInfo = this.getFeedingBehavior();
        if (feedingInfo)
            addPart(feedingInfo);
        // Reproductive strategy
        const reproductiveInfo = this.getReproductiveInformation();
        if (reproductiveInfo)
            addPart(reproductiveInfo);
        // Behavioral characteristics
        const behavioralInfo = this.getBehavioralCharacteristics();
        if (behavioralInfo)
            addPart(behavioralInfo);
        // Conservation status
        const conservationInfo = this.getConservationInformation();
        if (conservationInfo)
            addPart(conservationInfo);
        // Fallback if no specific information is available
        if (descriptionParts.length === 0) {
            addPart(`The ${this.commonName} (${this.binomialName}) is a member of the ${this.family} family within the order ${this.order}, representing an important component of marine ecosystems.`);
        }
        return descriptionParts.join(" ");
    }
    getTaxonomicIntroduction() {
        const familyDescriptions = {
            "Carcharhinidae": "requiem shark",
            "Lamnidae": "mackerel shark",
            "Sphyrnidae": "hammerhead shark",
            "Squalidae": "dogfish shark",
            "Hexanchidae": "cow shark",
            "Chlamydoselachidae": "frilled shark",
            "Heterodontidae": "bullhead shark",
            "Orectolobidae": "carpet shark",
            "Ginglymostomatidae": "nurse shark",
            "Rhincodontidae": "whale shark"
        };
        // Get the appropriate species type description
        let speciesTypeDesc = "shark";
        if (this.speciesType === SPECIES_TYPE.RAYS) {
            speciesTypeDesc = "ray";
        }
        else if (this.speciesType === SPECIES_TYPE.CHIMAERAS) {
            speciesTypeDesc = "chimaera";
        }
        const familyDesc = familyDescriptions[this.family] || speciesTypeDesc;
        return `The ${this.commonName} (${this.binomialName}) is a species of ${familyDesc} belonging to the ${this.order} order.`;
    }
    getPhysicalCharacteristics() {
        const traits = [];
        // Body shape and structure
        if (this.tags.includes(FLATTENED_BODY.YES)) {
            traits.push("possesses a dorsoventrally flattened body adapted for benthic life");
        }
        // Gill characteristics
        const gillCount = this.tags.find(tag => Object.values(NUM_GILLS).includes(tag));
        if (gillCount === NUM_GILLS.SIX) {
            traits.push("features six gill slits, a primitive characteristic shared with ancient shark lineages");
        }
        else if (gillCount === NUM_GILLS.SEVEN) {
            traits.push("possesses seven gill slits, an extremely rare trait among modern sharks");
        }
        // Dorsal fin characteristics
        const dorsalFinCount = this.tags.find(tag => Object.values(NUM_DORSAL_FINS).includes(tag));
        if (dorsalFinCount === NUM_DORSAL_FINS.ONE) {
            traits.push("has a single dorsal fin, distinguishing it from most shark species");
        }
        // Dorsal fin spines
        const dorsalSpines = this.tags.find(tag => Object.values(DORSAL_FIN_SPINES).includes(tag));
        if (dorsalSpines === DORSAL_FIN_SPINES.YES) {
            traits.push("bears spines on both dorsal fins for defense");
        }
        else if (dorsalSpines === DORSAL_FIN_SPINES.ONLY_ON_FIRST) {
            traits.push("features a spine on the first dorsal fin");
        }
        // Caudal fin shape
        const caudalShape = this.tags.find(tag => Object.values(CAUDAL_FIN_SHAPE).includes(tag));
        if (caudalShape === CAUDAL_FIN_SHAPE.HOMOCERCAL) {
            traits.push("has a homocercal caudal fin, providing efficient propulsion");
        }
        // Specialized features
        if (this.tags.includes(BIOLUMINESCENT.YES)) {
            traits.push("exhibits bioluminescence, producing light through specialized photophores");
        }
        if (this.tags.includes(NICTITATING_MEMBRANE.YES)) {
            traits.push("possesses a nictitating membrane, a protective third eyelid");
        }
        if (this.tags.includes(SPIRACLES.NO)) {
            traits.push("lacks spiracles, relying entirely on gill ventilation");
        }
        if (this.tags.includes(ANAL_FIN.NO)) {
            traits.push("lacks an anal fin, a characteristic of certain shark orders");
        }
        if (this.tags.includes(MOUTH_IN_FRONT_OF_EYES.MOUTH_IN_FRONT_OF_EYES)) {
            traits.push("has a terminal mouth position, with the mouth extending in front of the eyes");
        }
        if (this.tags.includes(PROXIMAL_DORSAL_FINS.YES)) {
            traits.push("features proximal dorsal fins positioned close together");
        }
        if (traits.length > 0) {
            return `Morphologically, this species ${traits.join(", ")}.`;
        }
        return "";
    }
    getHabitatInformation() {
        const habitatInfo = [];
        // Depth range
        if (this.depthRange) {
            const [minDepth, maxDepth] = this.depthRange.split('-').map(d => parseInt(d.trim()));
            if (maxDepth > 1000) {
                habitatInfo.push("inhabits deep oceanic waters");
            }
            else if (maxDepth > 200) {
                habitatInfo.push("occupies mid-water and continental slope habitats");
            }
            else {
                habitatInfo.push("frequents coastal and shallow waters");
            }
        }
        // Habitat preferences
        const habitatTags = this.tags.filter(tag => Object.values(HABITAT).includes(tag));
        if (habitatTags.length > 0) {
            const habitatNames = habitatTags.map(tag => tag.toLowerCase().replace(/_/g, ' '));
            habitatInfo.push(`preferring ${habitatNames.join(", ")} environments`);
        }
        // Ocean zones
        const oceanZoneTags = this.tags.filter(tag => Object.values(OCEAN_ZONE).includes(tag));
        if (oceanZoneTags.length > 0) {
            const zoneNames = oceanZoneTags.map(tag => tag.toLowerCase().replace(/_/g, ' '));
            habitatInfo.push(`primarily found in the ${zoneNames.join(" and ")} zones`);
        }
        // Water column position
        const waterColumnTags = this.tags.filter(tag => Object.values(WATER_COLUMN).includes(tag));
        if (waterColumnTags.length > 0) {
            const columnNames = waterColumnTags.map(tag => tag.toLowerCase().replace(/_/g, ' '));
            habitatInfo.push(`typically occupying the ${columnNames.join(" and ")}`);
        }
        if (habitatInfo.length > 0) {
            return `This species ${habitatInfo.join(", ")}.`;
        }
        return "";
    }
    getFeedingBehavior() {
        const feedingTag = this.tags.find(tag => Object.values(FEEDING_BEHAVIOR).includes(tag));
        if (!feedingTag)
            return "";
        const feedingDescriptions = {
            [FEEDING_BEHAVIOR.CARNIVOROUS]: "carnivorous diet",
            [FEEDING_BEHAVIOR.PISCIVOROUS]: "piscivorous diet, primarily consuming fish",
            [FEEDING_BEHAVIOR.PLANKTIVOROUS]: "planktivorous diet, feeding on plankton and small organisms",
            [FEEDING_BEHAVIOR.MOLLUSCIVOROUS]: "molluscivorous diet, specializing in mollusks and crustaceans",
            [FEEDING_BEHAVIOR.OMNIVOROUS]: "omnivorous diet"
        };
        return `It maintains a ${feedingDescriptions[feedingTag]}, playing a crucial role in marine food webs.`;
    }
    getReproductiveInformation() {
        const reproTag = this.tags.find(tag => Object.values(REPRODUCTIVE_STRATEGY).includes(tag));
        if (!reproTag)
            return "";
        const reproductiveDescriptions = {
            [REPRODUCTIVE_STRATEGY.OVIPAROUS]: "exhibits oviparous reproduction, laying leathery egg cases that develop externally",
            [REPRODUCTIVE_STRATEGY.VIVIPAROUS]: "demonstrates viviparous reproduction, giving birth to live young through placental connections",
            [REPRODUCTIVE_STRATEGY.OVOVIVIPAROUS]: "employs ovoviviparous reproduction, with eggs hatching internally before live birth",
            [REPRODUCTIVE_STRATEGY.EMBRYTROPHY]: "practices embryotrophy, providing additional nutrition to developing embryos",
            [REPRODUCTIVE_STRATEGY.OOPHAGY]: "exhibits oophagy, where embryos consume unfertilized eggs for nutrition",
            [REPRODUCTIVE_STRATEGY.EMBRYOPHAGY]: "demonstrates embryophagy, with embryos engaging in intrauterine cannibalism",
            [REPRODUCTIVE_STRATEGY.PARTHENOGENESIS]: "is capable of parthenogenesis, reproducing asexually when mates are unavailable",
            [REPRODUCTIVE_STRATEGY.UNKNOWN]: "has reproductive strategies that remain poorly understood"
        };
        return `Reproductively, this species ${reproductiveDescriptions[reproTag]}.`;
    }
    getBehavioralCharacteristics() {
        const behaviors = [];
        // Group behavior
        const groupBehaviorTag = this.tags.find(tag => Object.values(GROUP_BEHAVIOR).includes(tag));
        if (groupBehaviorTag === GROUP_BEHAVIOR.SCHOOLING) {
            behaviors.push("forms schools for enhanced foraging efficiency and predator avoidance");
        }
        else if (groupBehaviorTag === GROUP_BEHAVIOR.SOLITARY) {
            behaviors.push("exhibits solitary behavior, typically hunting and traveling alone");
        }
        // Specific behaviors
        const behaviorTag = this.tags.find(tag => Object.values(BEHAVIOR).includes(tag));
        if (behaviorTag === BEHAVIOR.MIGRATING) {
            behaviors.push("undertakes extensive migrations across ocean basins");
        }
        else if (behaviorTag === BEHAVIOR.BREACHING) {
            behaviors.push("is known for spectacular breaching behavior, leaping from the water");
        }
        if (behaviors.length > 0) {
            return `Behaviorally, this species ${behaviors.join(" and ")}.`;
        }
        return "";
    }
    getConservationInformation() {
        const conservationTag = this.tags.find(tag => Object.values(CONSERVATION_STATUS).includes(tag));
        if (!conservationTag)
            return "";
        const conservationDescriptions = {
            [CONSERVATION_STATUS.CR]: "is critically endangered, facing an extremely high risk of extinction in the wild",
            [CONSERVATION_STATUS.EN]: "is classified as endangered, requiring immediate conservation intervention",
            [CONSERVATION_STATUS.VU]: "is listed as vulnerable, with populations declining due to various threats",
            [CONSERVATION_STATUS.NT]: "is near threatened, approaching vulnerable status and requiring monitoring",
            [CONSERVATION_STATUS.LC]: "is of least concern, with stable populations and no immediate threats",
            [CONSERVATION_STATUS.DD]: "has data deficient status, with insufficient information for proper assessment",
            [CONSERVATION_STATUS.EW]: "is extinct in the wild, surviving only in captivity",
            [CONSERVATION_STATUS.EX]: "is extinct, with no known surviving individuals"
        };
        return `Conservation-wise, this species ${conservationDescriptions[conservationTag]}.`;
    }
}
